import { bisectLeft } from 'd3'
import { FC, useCallback, useRef, useState, MouseEvent, useMemo, HTMLAttributes, useEffect, TouchEvent } from 'react'
import { UseChartContext } from 'components/atoms/chart/useChart'
import styles from 'components/atoms/chart/ChartInteraction/ChartInteraction.styl'
import cn from 'classnames'

type ChartInteractionProps =
  & Omit<HTMLAttributes<HTMLDivElement>, 'onMouseMove' | 'onMouseLeave' | 'onChange'>
  & {
    context: UseChartContext
    onChange: (index: number) => void
  }
export const ChartInteraction: FC<ChartInteractionProps> = (props) => {
  const { children, context, className, onChange, ...restProps } = props

  const [hoveredIndex, setHoveredIndex] = useState(-1)

  const handleCords = useCallback((xCord: number) => {
    const { current } = rootRef
    if (!current) {
      return
    }

    const { left } = current.getBoundingClientRect()
    const xDate = context.scaleX.invert(xCord - left)
    let index = bisectLeft(context.xArray, xDate)

    const prevDate = context.xArray[index - 1]
    const nextDate = context.xArray[index]
    if (xDate - prevDate <= nextDate - xDate) {
      index--
    }

    const maxIndex = context.xArray.length - 1
    setHoveredIndex(Math.min(index, maxIndex))
  }, [context.scaleX, context.xArray])

  const rootRef = useRef<HTMLDivElement>(null)
  const handleMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    handleCords(event.pageX)
  }, [handleCords])

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(-1)
  }, [])

  const highlightIndex = useMemo(() => {
    return hoveredIndex === -1 ? context.xArray.length - 1 : hoveredIndex
  }, [hoveredIndex, context.xArray.length])

  useEffect(() => {
    onChange(highlightIndex)
  }, [highlightIndex, onChange])

  const handleTouchMove = useCallback<(event: TouchEvent<HTMLDivElement>) => void>((event) => {
    const touch = event.touches[0]
    if (!touch) {
      return
    }
    const { clientX } = touch
    handleCords(clientX)
  }, [handleCords])


  return (
    <div
      { ...restProps }
      className={ cn(styles.chartInteraction, className) }
      onMouseMove={ handleMouseMove }
      onMouseLeave={ handleMouseLeave }
      onTouchMove={ handleTouchMove }
      onTouchEnd={ handleMouseLeave }
      ref={ rootRef }
    >
      { children }
    </div>
  )
}
